import produce from 'immer';
import {SearchPageAction, SearchPageState, SearchPageTypeState} from './search-types';
import {ProdDocument, SearchSourceType, TabType} from '../search-types';
import {emptyReduxIndexedNormalizedState} from '../../app-types';
import {
    expandCollapseFacetGroup,
    expandFacetGroup,
    initialSearchFacetState,
    replaceFacetsFromSearch,
    sortFacets,
    updateSelectedFacets,
    updateSelectedFacetsByField
} from '../../facets/facetUtils';
import {createInitialSearchState} from '../search-config';
import {FacetValuesSortOrder, HibuFacetGroup} from '../../facets/facetTypes';

export const createInitialSearchPageState = (): SearchPageTypeState => ({
    historyEntries: [],
    savedSearches: undefined,
    savedSearchesSharedByOthers: undefined,
    showScrollButton: false,
    showFullDocument: false,
    updateDocumentInProgress: false,
    expandedProdDocument: undefined,
    docs: emptyReduxIndexedNormalizedState(),
    facets: initialSearchFacetState(),
    query: '',
    inDocumentQuery: undefined,
    queryPending: false,
    facetsPending: false,
    currentSortType: {field: '', order: ''},
    docOffset: 0,
    queryFields: undefined,
    spellCheckResult: '',
    resultFor: '',
    sources: [
        {
            name: SearchSourceType.ALL_DOCUMENTS,
            active: true,
        },
        {
            name: SearchSourceType.BOOKMARKS,
            active: false,
        },
    ],
    totalFound: null,
    lastSearchedQuery: '',
    facetListExpanded: true,
    documentDetailsExpanded: true,
    highlightId: undefined,
    documentQueryHighlights: [],
    documentSelectedTags: [],
    previewDocument: undefined,
    previewDocumentWithHighlights: undefined,
    showAnnotations: false,
    openResultTab: TabType.CONTENT,
    currentPageNumber: 1,
    pageRotations: [0],
    zoomFactor: 1,
    scrollTarget: null,
    resetTransform: false,
    numberOfPages: 1,
    sortTypes: undefined,
    isDocumentLoading: false,
    useAutoCorrection: true,
});

export function searchReducer(
    state: SearchPageState = createInitialSearchState(),
    action: SearchPageAction
): SearchPageState {
    return produce(state, (draft: SearchPageState) => {
        const searchPageType = (action as SearchPageAction).searchPageType;
        const searchPageState = draft[searchPageType];
        switch (action.type) {
            case 'SET_SEARCH_QUERY_FIELDS': {
                searchPageState.queryFields = action.queryFields;
                break;
            }
            case 'SEARCH_HISTORY_LOADED': {
                searchPageState.historyEntries = action.entries || [];
                break;
            }
            case 'SEARCH_SCROLL_BUTTON_VISIBILITY_CHANGED': {
                searchPageState.showScrollButton = action.showScrollButton;
                break;
            }
            case 'SEARCH_RESULT_SET_EXPANDED_DOCUMENT': {
                searchPageState.expandedProdDocument = action.doc as ProdDocument;
                break;
            }
            case 'SEARCH_COMPLETED': {
                const responseDocs = action.searchResult.docs as ProdDocument[];

                if (action.searchResult.shouldReplaceSearchResults) { //not executed
                    searchPageState.docs = emptyReduxIndexedNormalizedState();
                }

                addDocs(searchPageState, responseDocs);

                searchPageState.queryPending = false;
                searchPageState.totalFound = action.searchResult.totalFound;
                searchPageState.docOffset = action.searchResult.nextDocOffset as number;
                searchPageState.spellCheckResult = action.searchResult.spellCheckResult as string;
                searchPageState.resultFor = action.searchResult.resultFor as string;
                searchPageState.lastSearchedQuery = action.searchResult.lastSearchedQuery as string;
                break;
            }
            case 'FETCH_NEXT_PAGE_COMPLETED':
                addDocs(searchPageState, action.docs);
                searchPageState.queryPending = false;
                searchPageState.docOffset = searchPageState.docOffset + action.docs.length;
                break;
            // FACETS
            case 'SEARCH_FACETS_UPDATE':
                searchPageState.facetsPending = true;
                searchPageState.showScrollButton = false;
                searchPageState.docOffset = 0;
                searchPageState.docs = emptyReduxIndexedNormalizedState();
                updateSelectedFacetsByField(searchPageState.facets, action.fieldName, action.newSelectedFacetValues, action.containerFacets);
                break;
            case 'SEARCH_FACETS_STARTED':
                searchPageState.facetsPending = true;
                break;
            case 'FACETS_ALL_UPDATE':
                searchPageState.facetsPending = true;
                searchPageState.showScrollButton = false;
                searchPageState.docOffset = 0;
                searchPageState.docs = emptyReduxIndexedNormalizedState();
                updateSelectedFacets(searchPageState.facets, action.facetFilters, action.containerFacets);
                break;
            case 'SEARCH_FACETS_COMPLETED':
                replaceFacetsFromSearch(searchPageState.facets, action.searchResult.facets as HibuFacetGroup[]);
                sortFacets(searchPageState.facets, searchPageState.facets.facetValuesSortOrder || FacetValuesSortOrder.COUNT);

                searchPageState.facetsPending = false;
                break;
            case 'EXPAND_COLLAPSE_FACET_GROUP':
                expandCollapseFacetGroup(searchPageState.facets, action.fieldName);
                break;
            case 'EXPAND_FACET_GROUP':
                expandFacetGroup(searchPageState.facets, action.fieldName);
                break;
            case 'FACET_VALUES_SORT_ORDER_CHANGED':
                sortFacets(searchPageState.facets, action.facetSortField);
                break;

            // SEARCH
            case 'SEARCH_STARTED':
                searchPageState.query = action.query?.trim() || '';
                searchPageState.queryPending = true;
                searchPageState.showScrollButton = false;
                searchPageState.docOffset = 0;
                searchPageState.spellCheckResult = '';
                searchPageState.resultFor = '';
                searchPageState.docs = emptyReduxIndexedNormalizedState();
                searchPageState.documentDetailsExpanded = true;
                break;
            case 'SEARCH_RESET_FACETS': {
                searchPageState.facets = initialSearchFacetState();
                break;
            }
            case 'SEARCH_CHANGE_QUERY':
                searchPageState.query = action.query;
                break;
            case 'FETCH_NEXT_PAGE_STARTED':
                searchPageState.queryPending = true;
                break;
            case 'SEARCH_FAILED':
                searchPageState.queryPending = false;
                break;
            case 'SEARCH_SOURCE_SWITCHED': {
                const name = action.name;
                searchPageState.sources = searchPageState.sources.map((source) => {
                    return {
                        ...source,
                        active: source.name === name,
                    };
                });
                break;
            }
            case 'RESULT_SORT_CHANGED':
                searchPageState.currentSortType = action.sortType;
                break;
            case 'SEARCH_ADD_BOOKMARK': {
                const bookmarkDoc: ProdDocument | undefined = searchPageState.docs.byKey[action.doc.id];
                if (bookmarkDoc) {
                    bookmarkDoc.metadata.bookmarked = true;
                }
                break;
            }
            case 'SEARCH_REMOVE_BOOKMARK': {
                const bookmarkDoc: ProdDocument | undefined = searchPageState.docs.byKey[action.doc.id];
                if (bookmarkDoc) {
                    bookmarkDoc.metadata.bookmarked = false;
                }
                break;
            }
            case 'SEARCH_SET_SAVED_SEARCH_HISTORY': {
                searchPageState.savedSearches = action.savedSearches;
                searchPageState.savedSearchesSharedByOthers = action.sharedByOthers;
                break;
            }
            case 'EXPAND_FACET_LIST': {
                searchPageState.facetListExpanded = action.expanded;
                break;
            }
            case 'EXPAND_DOCUMENT_DETAILS': {
                searchPageState.documentDetailsExpanded = action.expanded;
                break;
            }
            case 'SWITCH_RESULT_DETAILS_TAB': {
                searchPageState.openResultTab = action.resultDetailsTab;
                break;
            }
            case 'SCROLL_TARGET_CHANGED': {
                searchPageState.currentPageNumber = action.newPageNumber ? action.newPageNumber : 1;
                searchPageState.scrollTarget = action.scrollTarget;
                break;
            }
            case 'RESET_TRANSFORM': {
                searchPageState.resetTransform = action.resetTransform;
                break;
            }
            case 'SELECTED_DOCUMENT_PAGE_CHANGED': {
                searchPageState.currentPageNumber = action.newPageNumber ? action.newPageNumber : 1;
                searchPageState.scrollTarget = null;
                break;
            }
            case 'SELECTED_DOCUMENT_CHANGED': {
                searchPageState.numberOfPages = action.numberOfPages;
                searchPageState.scrollTarget = null;
                break;
            }
            case 'CURRENT_PAGE_ROTATED': {
                searchPageState.pageRotations[searchPageState.currentPageNumber - 1] = action.rotation;
                break;
            }
            case 'CURRENT_DOCUMENT_ROTATIONS': {
                searchPageState.pageRotations = action.rotations;
                break;
            }
            case 'PREVIEW_DOCUMENT': {
                searchPageState.previewDocument = action.previewDocument;
                break;
            }
            case 'PREVIEW_DOCUMENT_WITH_HIGHLIGHTS': {
                searchPageState.previewDocumentWithHighlights = action.previewDocumentWithHighlights;
                break;
            }
            case 'SHOW_ANNOTATIONS': {
                searchPageState.showAnnotations = action.showAnnotations;
                break;
            }
            case 'SEARCH_CHANGE_IN_DOCUMENT_QUERY':
                searchPageState.inDocumentQuery = action.inDocumentQuery;
                break;
            case 'SET_DOCUMENT_QUERY_HIGHLIGHTS': {
                searchPageState.documentQueryHighlights = action.documentAnnotations;
                break;
            }
            case 'SET_DOCUMENT_SELECTED_TAGS': {
                searchPageState.documentSelectedTags = action.documentSelectedTags;
                break;
            }
            case 'SELECTED_HIGHLIGHT_ID': {
                searchPageState.highlightId = action.highlightId;
                break;
            }
            case 'SET_AVAILABLE_SORT_OPTIONS': {
                searchPageState.sortTypes = action.sortTypes;
                break;
            }
            case 'IS_LOADING_DOCUMENT': {
                searchPageState.isDocumentLoading = action.isLoading;
                break;
            }
            case 'UPDATE_AUTO_CORRECTION': {
                searchPageState.useAutoCorrection = action.useAutoCorrection;
                break;
            }
        }
    });
}

// TODO: make generalized util method for handling all ReduxNormalizedState or ReduxIndexedNormalizedState?
function addDocs(searchPageState: SearchPageTypeState, responseDocs: ProdDocument[]) {
    // when appending new documents (in case of load more while scrolling),
    // the index should always continue from the last document's index
    const indexOffset = searchPageState.docs.allKeys.length;
    for (let i = 0; i < responseDocs.length; i++) {
        const currentDoc = responseDocs[i];

        searchPageState.docs.byKey[currentDoc.id] = currentDoc; //
        searchPageState.docs.allKeys.push(currentDoc.id);
        searchPageState.docs.docsLengthIncludingDuplicates += 1 + (currentDoc.duplicates ? currentDoc.duplicates.length : 0); // the document + duplicates
        searchPageState.docs.index[currentDoc.id] = i + indexOffset;
    }
}
